import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { adminAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Users, Package, AlertTriangle, BarChart3, Shield, Clock, Tag, Trash2, Plus, CreditCard, Image as ImageIcon, FileText, Menu, X, Search, CheckCircle, XCircle, Store, Mail, MessageSquare, Bell, MapPin, ExternalLink, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import AdminOverview from '../components/admin/AdminOverview';
import AdminUsersTab from '../components/admin/AdminUsersTab';
import AdminListingsTab from '../components/admin/AdminListingsTab';
import AdminPaymentsTab from '../components/admin/AdminPaymentsTab';
import AdminBannersTab from '../components/admin/AdminBannersTab';
import AdminAuditTab from '../components/admin/AdminAuditTab';
import AdminDealersTab from '../components/admin/AdminDealersTab';
import AdminContactTab from '../components/admin/AdminContactTab';
import AdminChatTab from '../components/admin/AdminChatTab';
import AdminPushTab from '../components/admin/AdminPushTab';
import AdminAdsTab from '../components/admin/AdminAdsTab';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'moderation', label: 'Moderation', icon: Clock },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'listings', label: 'Listings', icon: Package },
  { id: 'dealers', label: 'Dealers', icon: Store },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'banners', label: 'Banners', icon: ImageIcon },
  { id: 'ads', label: 'Ads', icon: Megaphone },
  { id: 'push', label: 'Push', icon: Bell },
  { id: 'contact', label: 'Contact', icon: Mail },
  { id: 'chats', label: 'User Chats', icon: MessageSquare },
  { id: 'reports', label: 'Reports', icon: AlertTriangle },
  { id: 'suspicious', label: 'Suspicious', icon: Shield },
  { id: 'coupons', label: 'Coupons', icon: Tag },
  { id: 'audit', label: 'Audit Log', icon: FileText },
];

const AdminPanel = () => {
  const VALID_TABS = TABS.map((t) => t.id);
  const initialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      return q && VALID_TABS.includes(q) ? q : 'overview';
    } catch (_) { return 'overview'; }
  })();
  const [tab, setTab] = useState(initialTab);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [globalQ, setGlobalQ] = useState('');
  const [globalResults, setGlobalResults] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reports, setReports] = useState([]);
  const [suspicious, setSuspicious] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_percent: 10, max_uses: 100 });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!user.is_admin) { navigate('/'); toast.error('Admin access required'); return; }
    loadModerationData();
  }, [user, authLoading, navigate]);

  const loadModerationData = async () => {
    try {
      const [pendingRes, reportsRes, suspRes, couponsRes] = await Promise.all([
        adminAPI.getPendingReviews().catch(() => ({ data: [] })),
        adminAPI.getReports().catch(() => ({ data: [] })),
        adminAPI.getSuspiciousUsers().catch(() => ({ data: [] })),
        adminAPI.getCoupons().catch(() => ({ data: [] })),
      ]);
      setPendingReviews(pendingRes.data);
      setReports(reportsRes.data);
      setSuspicious(suspRes.data);
      setCoupons(couponsRes.data);
    } catch (e) { console.error(e); }
  };

  const runGlobalSearch = async (e) => {
    e?.preventDefault();
    if (!globalQ || globalQ.length < 2) { toast.error('Type at least 2 chars'); return; }
    try { const r = await adminAPI.globalSearch(globalQ); setGlobalResults(r.data); }
    catch (err) { toast.error('Search failed'); }
  };

  const handleApprove = async (id) => {
    try { await adminAPI.approveListing(id); toast.success('Approved'); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleReject = async (id) => {
    if (!rejectReason.trim()) { toast.error('Enter rejection reason'); return; }
    try { await adminAPI.rejectListing(id, rejectReason); toast.success('Rejected'); setRejectReason(''); setRejectingId(null); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleBulkApprove = async () => {
    if (bulkSelected.length === 0) return;
    if (!window.confirm(`Approve ${bulkSelected.length} listings?`)) return;
    try { const r = await adminAPI.bulkApprove(bulkSelected); toast.success(`Approved ${r.data.approved}`); setBulkSelected([]); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleBulkReject = async () => {
    if (bulkSelected.length === 0) return;
    const reason = window.prompt('Rejection reason for all:', 'Policy violation');
    if (!reason) return;
    try { const r = await adminAPI.bulkReject(bulkSelected, reason); toast.success(`Rejected ${r.data.rejected}`); setBulkSelected([]); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleUpdateReport = async (id, status) => {
    try { await adminAPI.updateReport(id, status); toast.success('Updated'); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleCreateCoupon = async () => {
    if (!newCoupon.code.trim()) { toast.error('Enter coupon code'); return; }
    try { await adminAPI.createCoupon(newCoupon); toast.success('Coupon created'); setNewCoupon({ code: '', discount_percent: 10, max_uses: 100 }); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleDeleteCoupon = async (id) => {
    try { await adminAPI.deleteCoupon(id); toast.success('Deleted'); loadModerationData(); }
    catch (e) { toast.error('Failed'); }
  };

  const onTabSelect = (id) => { setTab(id); setSidebarOpen(false); };

  if (authLoading || !user || !user.is_admin) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-[#1800ad]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Top bar (mobile + desktop) */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg bg-[#f0edff] text-[#1800ad]" data-testid="open-sidebar">
            <Menu size={18} />
          </button>
          <Link to="/" className="text-[#6B7280] hover:text-[#1800ad] text-xs sm:text-sm font-medium whitespace-nowrap hidden sm:inline">← Home</Link>
          <h1 className="text-lg sm:text-xl font-bold text-[#0A0B10]" data-testid="admin-title">Admin Panel</h1>
          <form onSubmit={runGlobalSearch} className="ml-auto flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input value={globalQ} onChange={(e) => setGlobalQ(e.target.value)} placeholder="Search everything…" data-testid="global-search-input"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm outline-none focus:border-[#1800ad]" />
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block">
          <nav className="bg-white rounded-xl border border-gray-100 p-2 sticky top-20" data-testid="admin-sidebar-desktop">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => onTabSelect(id)} data-testid={`sidebar-tab-${id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${tab === id ? 'bg-[#1800ad] text-white' : 'text-[#4B5563] hover:bg-[#f0edff]'}`}>
                <Icon size={14} />
                <span>{label}</span>
                {id === 'moderation' && pendingReviews.length > 0 && (
                  <span className="ml-auto bg-[#ff8500] text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">{pendingReviews.length}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Sidebar (mobile drawer) */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}>
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl p-3" onClick={(e) => e.stopPropagation()} data-testid="admin-sidebar-mobile">
              <div className="flex items-center justify-between mb-3 px-2">
                <h2 className="font-bold text-[#0A0B10]">Admin</h2>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded bg-gray-100"><X size={14} /></button>
              </div>
              {TABS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => onTabSelect(id)} data-testid={`sidebar-tab-mobile-${id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold mb-1 ${tab === id ? 'bg-[#1800ad] text-white' : 'text-[#4B5563] hover:bg-[#f0edff]'}`}>
                  <Icon size={15} /> {label}
                  {id === 'moderation' && pendingReviews.length > 0 && (
                    <span className="ml-auto bg-[#ff8500] text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">{pendingReviews.length}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="min-w-0">
          {/* Global search results overlay */}
          {globalResults && (
            <div className="bg-white rounded-xl border border-gray-100 p-3 mb-4" data-testid="global-search-results">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-[#0A0B10]">Search results for "{globalQ}"</p>
                <button onClick={() => { setGlobalResults(null); setGlobalQ(''); }} className="p-1 rounded bg-gray-100"><X size={14} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="font-bold text-[#1800ad] mb-1">Users ({globalResults.users.length})</p>
                  {globalResults.users.slice(0, 5).map((u) => (<div key={u.user_id} className="bg-[#f8f9fa] rounded p-1.5 mb-1 truncate">{u.name || u.email || u.user_id}</div>))}
                </div>
                <div>
                  <p className="font-bold text-[#ff8500] mb-1">Listings ({globalResults.listings.length})</p>
                  {globalResults.listings.slice(0, 5).map((l) => (<div key={l.listing_id} className="bg-[#f8f9fa] rounded p-1.5 mb-1 truncate">{l.title}</div>))}
                </div>
                <div>
                  <p className="font-bold text-amber-600 mb-1">Reports ({globalResults.reports.length})</p>
                  {globalResults.reports.slice(0, 5).map((r) => (<div key={r.report_id} className="bg-[#f8f9fa] rounded p-1.5 mb-1 truncate">{r.reason}</div>))}
                </div>
              </div>
            </div>
          )}

          {tab === 'overview' && <AdminOverview onNavigateTab={setTab} />}
          {tab === 'users' && <AdminUsersTab />}
          {tab === 'listings' && <AdminListingsTab />}
          {tab === 'dealers' && <AdminDealersTab />}
          {tab === 'payments' && <AdminPaymentsTab />}
          {tab === 'banners' && <AdminBannersTab />}
          {tab === 'ads' && <AdminAdsTab />}
          {tab === 'push' && <AdminPushTab />}
          {tab === 'contact' && <AdminContactTab />}
          {tab === 'chats' && <AdminChatTab />}
          {tab === 'audit' && <AdminAuditTab />}

          {tab === 'moderation' && (
            <div className="space-y-3" data-testid="moderation-section">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-bold text-[#0A0B10]">Pending Reviews ({pendingReviews.length})</h2>
                {bulkSelected.length > 0 && (
                  <div className="flex gap-2">
                    <button onClick={handleBulkApprove} className="bg-green-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="bulk-approve">Approve {bulkSelected.length}</button>
                    <button onClick={handleBulkReject} className="bg-red-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="bulk-reject">Reject {bulkSelected.length}</button>
                  </div>
                )}
              </div>
              {pendingReviews.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-sm text-[#9CA3AF]">No pending listings</div>
              ) : pendingReviews.map((l) => (
                <div key={l.listing_id} className="bg-white rounded-xl p-3 border border-gray-100" data-testid={`pending-${l.listing_id}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={bulkSelected.includes(l.listing_id)}
                      onChange={(e) => setBulkSelected(e.target.checked ? [...bulkSelected, l.listing_id] : bulkSelected.filter((id) => id !== l.listing_id))}
                      data-testid={`bulk-${l.listing_id}`} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-bold text-sm text-[#0A0B10] break-words">{l.title}</p>
                        <Link
                          to={`/listing/${l.listing_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`open-listing-${l.listing_id}`}
                          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#1800ad] hover:underline"
                        >
                          <ExternalLink size={12} /> Open
                        </Link>
                      </div>

                      {Array.isArray(l.images) && l.images.length > 0 && (
                        <div className="flex gap-2 mt-2 overflow-x-auto pb-1" data-testid={`preview-images-${l.listing_id}`}>
                          {l.images.map((img, i) => (
                            <button
                              key={img + i}
                              type="button"
                              onClick={() => setPreviewImage(`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`)}
                              className="shrink-0 rounded-lg overflow-hidden border border-gray-200 hover:border-[#1800ad] transition-colors"
                              data-testid={`preview-image-${l.listing_id}-${i}`}
                            >
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`}
                                alt={`${l.title} ${i + 1}`}
                                className="h-20 w-20 object-cover"
                                loading="lazy"
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {l.description && (
                        <div
                          data-testid={`preview-description-${l.listing_id}`}
                          className="text-xs text-[#6B7280] leading-relaxed mt-2 [&_p]:my-1 [&_strong]:text-[#0A0B10] [&_strong]:font-semibold"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(l.description || '', {
                              ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span', 'ul', 'ol', 'li'],
                              ALLOWED_ATTR: ['style'],
                            }),
                          }}
                        />
                      )}

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#4B5563] mt-2">
                        <span className="font-bold text-[#0A0B10]">₹{l.price?.toLocaleString?.() ?? l.price}</span>
                        {(l.location || l.city) && (
                          <span className="inline-flex items-center gap-1"><MapPin size={12} />{l.location || l.city}</span>
                        )}
                        {(l.category || l.category_id) && (
                          <span className="inline-flex items-center gap-1"><Tag size={12} />{l.category || l.category_id}</span>
                        )}
                        <span className="text-[#9CA3AF]">by {l.seller?.name || l.user_id}</span>
                      </div>
                    </div>
                  </div>
                  {rejectingId === l.listing_id ? (
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason"
                        className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid={`reject-reason-${l.listing_id}`} />
                      <button onClick={() => handleReject(l.listing_id)} className="bg-red-600 text-white rounded-lg px-3 py-2 text-xs font-bold" data-testid={`reject-confirm-${l.listing_id}`}>Confirm Reject</button>
                      <button onClick={() => { setRejectingId(null); setRejectReason(''); }} className="bg-gray-200 text-[#4B5563] rounded-lg px-3 py-2 text-xs font-bold">Cancel</button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => handleApprove(l.listing_id)} className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1" data-testid={`approve-${l.listing_id}`}>
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button onClick={() => setRejectingId(l.listing_id)} className="bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1" data-testid={`reject-${l.listing_id}`}>
                        <XCircle size={12} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'reports' && (
            <div className="space-y-3" data-testid="reports-section">
              <h2 className="text-base sm:text-lg font-bold text-[#0A0B10]">Reports ({reports.length})</h2>
              {reports.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-sm text-[#9CA3AF]">No reports</div>
              ) : reports.map((r) => (
                <div key={r.report_id} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3" data-testid={`report-${r.report_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#0A0B10]">{r.reason}</p>
                    <p className="text-xs text-[#6B7280] truncate">{r.details}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-1">By {r.reporter_id} · status: {r.status}</p>
                  </div>
                  <select value={r.status} onChange={(e) => handleUpdateReport(r.report_id, e.target.value)} data-testid={`report-status-${r.report_id}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs">
                    <option value="pending">pending</option>
                    <option value="reviewed">reviewed</option>
                    <option value="dismissed">dismissed</option>
                    <option value="actioned">actioned</option>
                  </select>
                </div>
              ))}
            </div>
          )}

          {tab === 'suspicious' && (
            <div className="space-y-3" data-testid="suspicious-section">
              <h2 className="text-base sm:text-lg font-bold text-[#0A0B10]">Suspicious Users ({suspicious.length})</h2>
              {suspicious.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-sm text-[#9CA3AF]">No suspicious users</div>
              ) : suspicious.map((u) => (
                <div key={u.user_id} className="bg-white rounded-xl p-3 border border-gray-100" data-testid={`susp-${u.user_id}`}>
                  <p className="font-bold text-sm text-[#0A0B10]">{u.name || u.user_id}</p>
                  <p className="text-xs text-[#6B7280]">{u.email} · flags: <strong className="text-red-600">{u.suspicious_flags}</strong></p>
                </div>
              ))}
            </div>
          )}

          {tab === 'coupons' && (
            <div className="space-y-3" data-testid="coupons-section">
              <div className="bg-white rounded-xl p-3 border border-gray-100">
                <p className="font-bold text-sm text-[#0A0B10] mb-2">Create coupon</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="CODE"
                    data-testid="coupon-code-input" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                  <input type="number" value={newCoupon.discount_percent} onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseInt(e.target.value) || 0 })}
                    placeholder="% off" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm" data-testid="coupon-percent-input" />
                  <input type="number" value={newCoupon.max_uses} onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) || 0 })}
                    placeholder="Max uses" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm" data-testid="coupon-uses-input" />
                  <button onClick={handleCreateCoupon} className="bg-[#1800ad] text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1" data-testid="create-coupon-btn">
                    <Plus size={14} /> Create
                  </button>
                </div>
              </div>
              {coupons.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-sm text-[#9CA3AF]">No coupons</div>
              ) : coupons.map((c) => (
                <div key={c.coupon_id} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3" data-testid={`coupon-${c.coupon_id}`}>
                  <div className="flex-1">
                    <p className="font-bold text-[#1800ad] text-base font-mono">{c.code}</p>
                    <p className="text-xs text-[#6B7280]">{c.discount_percent}% off · Used {c.used_count}/{c.max_uses}</p>
                  </div>
                  <button onClick={() => handleDeleteCoupon(c.coupon_id)} className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
          data-testid="image-preview-overlay"
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/90 text-[#0A0B10] hover:bg-white"
            data-testid="image-preview-close"
          >
            <X size={20} />
          </button>
          <img
            src={previewImage}
            alt="Advertisement preview"
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
